import {WriteButton} from '../common/buttons/WriteActions';
import {getCurrencySymbol} from '../../common/currency';
import {FormEvent, useEffect, useMemo, useState} from 'react';
import {Alert, Box, Button, CircularProgress, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, SelectChangeEvent, Snackbar, Stack, TextField, Tooltip} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {ContentSkeleton} from '../common/loading/ContentSkeleton';
import {IUser, IUserSettings} from '../../models/IUser';
import {userAPI} from '../../services/UserServices';
import {getApiErrorKind, getApiErrorMessage, getApiFieldErrors, getApiTraceId} from '../../common/apiErrors';
import TraceIdFeedback from '../feedback/TraceIdFeedback';
import {PageHeader} from '../common/layout/PageHeader';
import {Surface} from '../common/layout/Surface';
import AccountDeletionPanel from './AccountDeletionPanel';

const supportedLanguages = [
    {value: 'en', label: 'English'},
    {value: 'pl', label: 'Polski'},
    {value: 'ru', label: 'Русский'},
    {value: 'uk', label: 'Українська'},
];

const supportedCurrencies = [
    {value: 'PLN', label: 'PLN — zł'},
    {value: 'EUR', label: 'EUR — €'},
    {value: 'USD', label: 'USD — $'},
    {value: 'UAH', label: 'UAH — ₴'},
];

const settingsFromUser = (user: IUser, localLanguage: string): IUserSettings => ({
    workshopName: user.workshopName ?? '',
    currency: user.currency || 'PLN',
    language: user.language || localLanguage,
    dateFormat: user.dateFormat || 'dd.MM.yyyy',
    defaultPrepaymentPercent: user.defaultPrepaymentPercent ?? 0,
    additionalRecipeCost: user.additionalRecipeCost ?? 0,
});

export default function Settings() {
    const {t, i18n} = useAppTranslation();
    const {data: user, error: loadError, isLoading, isError, refetch} = userAPI.useGetUserDataQuery();
    const [updateUser, updateState] = userAPI.useUpdateUserMutation();
    const [form, setForm] = useState<IUserSettings | null>(null);
    const [savedForm, setSavedForm] = useState<IUserSettings | null>(null);
    const [additionalRecipeCostInput, setAdditionalRecipeCostInput] = useState('0');
    const [showSuccess, setShowSuccess] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});
    const [traceId, setTraceId] = useState('');
    const [serverToast, setServerToast] = useState('');

    useEffect(() => {
        if (!user) return;
        const localLanguage = (localStorage.getItem('language') || i18n.resolvedLanguage || i18n.language || 'pl').split('-')[0];
        const nextForm = settingsFromUser(user, localLanguage);
        setForm(nextForm);
        setSavedForm(nextForm);
        setAdditionalRecipeCostInput(String(nextForm.additionalRecipeCost));
    }, [user, i18n.language, i18n.resolvedLanguage]);

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(savedForm), [form, savedForm]);
    const workshopNameError = form !== null && !form.workshopName.trim();
    const normalizedAdditionalRecipeCost = additionalRecipeCostInput.replace(',', '.');
    const parsedAdditionalRecipeCost = Number(normalizedAdditionalRecipeCost);
    const additionalRecipeCostError = additionalRecipeCostInput.trim() === ''
        || !/^\d+(?:[.,]\d*)?$/.test(additionalRecipeCostInput)
        || !Number.isFinite(parsedAdditionalRecipeCost)
        || parsedAdditionalRecipeCost < 0;

    const changeSelect = (field: 'currency' | 'language') => (event: SelectChangeEvent) => {
        setForm((current) => current ? {...current, [field]: event.target.value} : current);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!form || !user || workshopNameError || additionalRecipeCostError) return;

        setSaveError('');
        setServerFieldErrors({});
        setTraceId('');
        try {
            const updatedUser = await updateUser({
                ...user,
                ...form,
                workshopName: form.workshopName.trim(),
                additionalRecipeCost: parsedAdditionalRecipeCost,
            }).unwrap();
            const updatedSettings = settingsFromUser(updatedUser, form.language);
            setForm(updatedSettings);
            setSavedForm(updatedSettings);
            setAdditionalRecipeCostInput(String(updatedSettings.additionalRecipeCost));
            localStorage.setItem('language', updatedUser.language);
            await i18n.changeLanguage(updatedUser.language);
            setShowSuccess(true);
        } catch (error) {
            const kind = getApiErrorKind(error);
            const fieldErrors = getApiFieldErrors(error, t);
            setServerFieldErrors(kind === 'validation' ? fieldErrors : {});
            if (kind === 'server') setServerToast(getApiErrorMessage(error, t));
            else if (kind !== 'validation' || Object.keys(fieldErrors).length === 0) setSaveError(getApiErrorMessage(error, t));
            setTraceId(getApiTraceId(error));
        }
    };

    if (isLoading || (!form && !isError)) {
        return <ContentSkeleton variant="form"/>;
    }

    if (isError || !form) {
        return (
            <Box sx={{width: '100%'}}>
                <Alert severity="error" action={<Button color="inherit" onClick={() => refetch()}>{t('settings:retry')}</Button>}>
                    {getApiErrorMessage(loadError, t)}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{width: '100%'}}>
            <PageHeader title={t('navigation:settings')} subtitle={t('settings:description')}/>

            <Surface component="form" onSubmit={handleSubmit} sx={{p: {xs: 2, sm: 3}}}>
                <Stack spacing={3}>
                    <TextField required fullWidth label={t('settings:workshop_name')} value={form.workshopName}
                               onChange={(event) => setForm({...form, workshopName: event.target.value})}
                               error={workshopNameError || !!serverFieldErrors.workshopName} helperText={workshopNameError ? t('settings:workshop_name_required') : serverFieldErrors.workshopName || ' '}
                               inputProps={{maxLength: 200}}/>

                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 3}}>
                        <FormControl fullWidth>
                            <InputLabel id="settings-currency-label">{t('settings:currency')}</InputLabel>
                            <Select labelId="settings-currency-label" label={t('settings:currency')} value={form.currency} onChange={changeSelect('currency')}>
                                {supportedCurrencies.map((currency) => <MenuItem key={currency.value} value={currency.value}>{currency.label}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel id="settings-language-label">{t('settings:language')}</InputLabel>
                            <Select labelId="settings-language-label" label={t('settings:language')} value={form.language} onChange={changeSelect('language')}>
                                {supportedLanguages.map((language) => <MenuItem key={language.value} value={language.value}>{language.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>

                    <TextField fullWidth type="text" label={t('settings:additional_recipe_cost')}
                               value={additionalRecipeCostInput}
                               onChange={(event) => {
                                   const value = event.target.value;
                                   setAdditionalRecipeCostInput(value);
                                   const parsedValue = Number(value.replace(',', '.'));
                                   if (/^\d+(?:[.,]\d*)?$/.test(value) && Number.isFinite(parsedValue)) {
                                       setForm({...form, additionalRecipeCost: parsedValue});
                                   }
                               }}
                               onBlur={() => {
                                   if (!additionalRecipeCostError) setAdditionalRecipeCostInput(String(parsedAdditionalRecipeCost));
                               }}
                               error={additionalRecipeCostError || !!serverFieldErrors.additionalRecipeCost}
                               helperText={additionalRecipeCostError
                                   ? t('settings:additional_recipe_cost_error')
                                   : serverFieldErrors.additionalRecipeCost || t('settings:additional_recipe_cost_hint')}
                               inputProps={{inputMode: 'decimal'}}
                               InputProps={{
                                   endAdornment: (
                                       <Stack direction="row" alignItems="center" spacing={0.5}>
                                           <Tooltip title={t('settings:additional_recipe_cost_tooltip')} arrow>
                                               <IconButton size="small" aria-label={t('settings:additional_recipe_cost_tooltip')}>
                                                   <InfoOutlinedIcon fontSize="small"/>
                                               </IconButton>
                                           </Tooltip>
                                           <InputAdornment position="end">{getCurrencySymbol(form.currency)}</InputAdornment>
                                       </Stack>
                                   ),
                               }}/>

                    {saveError && <Alert severity="error">{saveError}<TraceIdFeedback traceId={traceId}/></Alert>}

                    <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                        <WriteButton type="submit" variant="contained"
                                startIcon={updateState.isLoading ? <CircularProgress color="inherit" size={18}/> : <SaveOutlinedIcon/>}
                                disabled={!isDirty || workshopNameError || additionalRecipeCostError || updateState.isLoading}
                                sx={{width: {xs: '100%', sm: 'auto'}}}>
                            {updateState.isLoading ? t('settings:saving') : t('settings:save')}
                        </WriteButton>
                    </Box>
                </Stack>
            </Surface>
            <AccountDeletionPanel/>

            <Snackbar open={showSuccess} autoHideDuration={4000} onClose={() => setShowSuccess(false)} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Alert severity="success" onClose={() => setShowSuccess(false)} sx={{width: '100%'}}>{t('settings:save_success')}</Alert>
            </Snackbar>
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Alert severity="error" onClose={() => setServerToast('')} sx={{width: '100%'}}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert>
            </Snackbar>
        </Box>
    );
}
